import React from 'react';
import { AlertCircle, Zap, Upload, Cpu, TrendingUp } from 'lucide-react';
import { knowMore } from '../data/mock';

const KnowMore = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6">
              Deep Dive into Momentedge
            </h1>
            <p className="text-xl md:text-2xl text-slate-600 leading-relaxed">
              Understand the problem we're solving and how our platform works under the hood.
            </p>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center space-x-4 mb-8">
              <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center">
                <AlertCircle className="w-7 h-7 text-red-600" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                {knowMore.problem.title}
              </h2>
            </div>
            
            <div className="bg-slate-50 rounded-2xl p-8 md:p-12">
              <ul className="space-y-6">
                {knowMore.problem.points.map((point, index) => (
                  <li key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-slate-900 text-white rounded-lg flex items-center justify-center font-bold text-sm mt-1">
                      {index + 1}
                    </div>
                    <p className="text-lg text-slate-700 leading-relaxed pt-1">
                      {point}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {knowMore.solution.title}
              </h2>
              <p className="text-xl text-slate-600">
                A four-step process that transforms how you collect robotics data
              </p>
            </div>

            <div className="space-y-8">
              {knowMore.solution.steps.map((step, index) => {
                const icons = [Cpu, Zap, Upload, TrendingUp];
                const IconComponent = icons[index];
                
                return (
                  <div
                    key={step.step}
                    className="bg-white rounded-2xl p-8 md:p-10 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex flex-col md:flex-row md:items-start md:space-x-8">
                      <div className="flex-shrink-0 mb-6 md:mb-0">
                        <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center text-white mb-4">
                          <IconComponent className="w-8 h-8" />
                        </div>
                        <div className="text-4xl font-bold text-slate-200">
                          {String(step.step).padStart(2, '0')}
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-slate-900 mb-4">
                          {step.title}
                        </h3>
                        <p className="text-lg text-slate-700 leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits/Impact Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {knowMore.benefits.title}
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Real results that transform your autonomous systems development
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {knowMore.benefits.metrics.map((metric, index) => (
              <div
                key={index}
                className="bg-slate-50 rounded-2xl p-8 text-center transform hover:scale-105 transition-transform"
              >
                <div className="text-5xl md:text-6xl font-bold text-slate-900 mb-4">
                  {metric.value}
                </div>
                <p className="text-slate-700 font-medium leading-snug">
                  {metric.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Details Section */}
      <section className="py-16 md:py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
              Technical Highlights
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-slate-800 rounded-2xl p-8">
                <h3 className="text-xl font-bold mb-4">Edge Processing</h3>
                <p className="text-slate-300 leading-relaxed">
                  Lightweight agents run directly on your robots with minimal CPU/GPU overhead, analyzing streams in real-time without cloud dependency.
                </p>
              </div>
              
              <div className="bg-slate-800 rounded-2xl p-8">
                <h3 className="text-xl font-bold mb-4">Configurable Triggers</h3>
                <p className="text-slate-300 leading-relaxed">
                  Define custom rules based on sensor data, ML model predictions, or business logic to capture exactly what your models need.
                </p>
              </div>
              
              <div className="bg-slate-800 rounded-2xl p-8">
                <h3 className="text-xl font-bold mb-4">Multi-Sensor Support</h3>
                <p className="text-slate-300 leading-relaxed">
                  Works with cameras, LiDAR, radar, IMU, GPS, and custom sensors with automatic synchronization and calibration preservation.
                </p>
              </div>
              
              <div className="bg-slate-800 rounded-2xl p-8">
                <h3 className="text-xl font-bold mb-4">Cloud-Native Architecture</h3>
                <p className="text-slate-300 leading-relaxed">
                  Seamlessly integrates with AWS, GCP, Azure, and private cloud infrastructure with built-in compression and encryption.
                </p>
              </div>
            </div>

            <div className="mt-12 text-center">
              <a
                href="mailto:hello@momentedge.xyz"
                className="inline-flex items-center px-8 py-4 bg-white text-slate-900 rounded-lg font-semibold hover:bg-slate-100 transition-all shadow-lg hover:shadow-xl"
              >
                Request Technical Documentation
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default KnowMore;
